import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput,
  TouchableOpacity, ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { createProfile, savePreferences, getStates, getDistricts, getCommunities, getProfileByUserId } from '../services/api';
import { THEME } from '../components';
import { DP } from '../components/DropdownPicker';
import { useAuthContext } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { RELIGIONS, MARITAL_STATUSES } from '../constants';
import type { StateResponse, DistrictResponse, CommunitySurnameResponse } from '../types';

const BL = (o: { en: string; hi: string }) => (o.hi ? `${o.en}  ${o.hi}` : o.en);

const OnboardingScreen: React.FC = () => {
  const { user, setHasProfile, relogin, logout } = useAuthContext();
  const { t } = useLanguage();

  // Every current login method (Google, email/password, Apple) returns an
  // email via Auth0, so this is pre-filled and shown read-only for now.
  // Once a login method that doesn't provide one (e.g. phone/OTP) exists,
  // this should become editable/required for that case specifically.
  const email = user?.email ?? '';

  const [gender, setGender] = useState<'MALE' | 'FEMALE' | null>(null);
  // Pre-filled from Sign in with Apple/Google when they provide a name —
  // Apple's review guidelines (4 - Design) require not re-asking for info
  // Sign in with Apple already gave via the Authentication Services
  // framework. Stays editable since email/password signups have no name to
  // pre-fill and still need to type one in.
  const [name, setName] = useState(user?.name ?? '');
  const [contactNo, setContactNo] = useState('');
  const [religion, setReligion] = useState('');
  const [caste, setCaste] = useState('');
  const [otherCaste, setOtherCaste] = useState(false);
  const [maritalStatus, setMaritalStatus] = useState('');
  const [dob, setDob] = useState('');
  const [stateName, setStateName] = useState('');
  const [district, setDistrict] = useState('');
  const [saving, setSaving] = useState(false);
  const [emailConflict, setEmailConflict] = useState(false);

  const [states, setStates] = useState<StateResponse[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [districts, setDistricts] = useState<DistrictResponse[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [communities, setCommunities] = useState<CommunitySurnameResponse[]>([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);

  useEffect(() => {
    setLoadingStates(true);
    getStates()
      .then(res => setStates(res.data))
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load states' }))
      .finally(() => setLoadingStates(false));

    setLoadingCommunities(true);
    getCommunities()
      .then(res => setCommunities(res.data))
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load communities' }))
      .finally(() => setLoadingCommunities(false));
  }, []);

  const onReligionSelected = (selected: string) => {
    setReligion(prev => {
      const next = prev === selected ? '' : selected;
      setCaste('');
      setOtherCaste(false);
      return next;
    });
  };

  const onStateSelected = (selected: string) => {
    setStateName(selected);
    setDistrict('');
    setDistricts([]);
    const found = states.find(s => s.en === selected);
    if (!found) return;
    setLoadingDistricts(true);
    getDistricts(found.stateCode)
      .then(res => setDistricts(res.data))
      .catch(() => Toast.show({ type: 'error', text1: 'Failed to load districts' }))
      .finally(() => setLoadingDistricts(false));
  };

  const dobValid = (() => {
    const m = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return false;
    const [, ddStr, mmStr, yyyyStr] = m;
    const dd = Number(ddStr), mm = Number(mmStr), yyyy = Number(yyyyStr);
    const parsed = new Date(yyyy, mm - 1, dd);
    if (parsed.getDate() !== dd || parsed.getMonth() !== mm - 1 || parsed.getFullYear() !== yyyy) return false;
    const age = Math.floor((Date.now() - parsed.getTime()) / (365.25 * 24 * 3600 * 1000));
    return age >= 18 && age <= 60;
  })();

  const stateLabels: Record<string, string> = {};
  states.forEach(s => { stateLabels[s.en] = BL(s); });
  const districtLabels: Record<string, string> = {};
  districts.forEach(d => { districtLabels[d.en] = BL(d); });

  const activeCommunities = communities.filter(c => c.active && (!religion || c.religion === religion));
  const casteOptions = [...activeCommunities.map(c => c.community.en), 'Other'];
  const casteLabels: Record<string, string> = {};
  activeCommunities.forEach(c => { casteLabels[c.community.en] = BL(c.community); });

  const canSubmit = !!gender && name.trim().length >= 2 && !!email && /^[0-9]{10}$/.test(contactNo)
    && !!religion && !!maritalStatus && dobValid && !!stateName && !!district;

  const handleGetStarted = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      // Onboarding collects everything the backend requires for submission,
      // so addProfile() puts this straight into PENDING_APPROVAL — no
      // separate submit step to remember or to fail independently.
      await createProfile({
        name: name.trim(),
        gender,
        contactNo,
        email,
        religion,
        ...(caste.trim() ? { caste: caste.trim() } : {}),
        maritalStatus,
        dob,
        stateName,
        district,
      });

      // Save religion as a preference so PreferencesScreen picks it up — best
      // effort only, must not block entry into the app if it fails (the
      // profile itself is already created/submitted by this point).
      try {
        await savePreferences({ preferredReligions: [religion] } as any);
      } catch (prefErr) {
        console.log('[Onboarding] savePreferences failed:', prefErr);
      }

      // Persist search defaults — same best-effort treatment.
      try {
        await AsyncStorage.multiSet([
          ['pref_looking_for', gender === 'MALE' ? 'FEMALE' : 'MALE'],
          ['pref_search_religion', religion],
        ]);
      } catch (storageErr) {
        console.log('[Onboarding] AsyncStorage.multiSet failed:', storageErr);
      }

      setHasProfile(true);
    } catch (e: any) {
      console.log('[Onboarding] error — status:', e?.status, 'message:', e?.message, 'response.status:', e?.response?.status);
      if (e?.status === 409 || e?.response?.status === 409) {
        // Two different things surface as this same 409: a genuine conflict
        // (email already belongs to a different Auth0 account), or a
        // same-user duplicate — e.g. a double-tap on "Get Started" firing
        // two requests, where the first already succeeded. Ask the backend
        // which one this is rather than assuming — if a profile now exists
        // under this exact user, it's ours, so just proceed into the app.
        if (user?.sub) {
          try {
            await getProfileByUserId(encodeURIComponent(user.sub));
            setHasProfile(true);
            return;
          } catch {
            // No profile under this user id — genuine different-account conflict.
          }
        }
        setEmailConflict(true);
      } else {
        Toast.show({ type: 'error', text1: 'Could not create profile', text2: (e as Error).message });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">

        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.appName}>JeevanSanyog</Text>
          <Text style={styles.tagline}>Let's set up your profile</Text>
          <Text style={styles.subtitle}>Just a few details to get you started</Text>
        </View>

        {/* Gender */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>I am a <Text style={styles.required}>*</Text></Text>
          <View style={styles.genderRow}>
            {(['MALE', 'FEMALE'] as const).map(g => (
              <TouchableOpacity
                key={g}
                style={[styles.genderCard, gender === g && styles.genderCardActive]}
                onPress={() => setGender(g)}
                activeOpacity={0.8}>
                <Text style={styles.genderIcon}>{g === 'MALE' ? '👨' : '👩'}</Text>
                <Text style={[styles.genderLabel, gender === g && styles.genderLabelActive]}>
                  {g === 'MALE' ? 'Groom' : 'Bride'}
                </Text>
                {gender === g && (
                  <View style={styles.genderCheck}>
                    <Icon name="checkmark-circle" size={20} color={THEME.primary} />
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Name */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Full Name <Text style={styles.required}>*</Text></Text>
          <TextInput
            style={styles.input}
            value={name}
            onChangeText={setName}
            placeholder="Enter your full name"
            placeholderTextColor={THEME.textLight}
            autoCapitalize="words"
            returnKeyType="done"
          />
        </View>

        {/* Email Address — read-only for now, see comment above `email` */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Email Address <Text style={styles.required}>*</Text></Text>
          <TextInput
            style={[styles.input, styles.inputReadonly]}
            value={email}
            editable={false}
          />
        </View>

        {/* Contact Number */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Mobile Number <Text style={styles.required}>*</Text></Text>
          <TextInput
            style={styles.input}
            value={contactNo}
            onChangeText={v => setContactNo(v.replace(/\D/g, '').slice(0, 10))}
            placeholder="10-digit mobile number"
            placeholderTextColor={THEME.textLight}
            keyboardType="phone-pad"
            maxLength={10}
            returnKeyType="done"
          />
          {contactNo.length > 0 && contactNo.length < 10 && (
            <Text style={styles.fieldHint}>Enter 10-digit number</Text>
          )}
        </View>

        {/* Date of Birth */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Date of Birth (DD/MM/YYYY) <Text style={styles.required}>*</Text></Text>
          <TextInput
            style={styles.input}
            value={dob}
            onChangeText={v => {
              const digits = v.replace(/\D/g, '').slice(0, 8);
              let formatted = digits;
              if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
              else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
              setDob(formatted);
            }}
            placeholder="DD/MM/YYYY"
            placeholderTextColor={THEME.textLight}
            keyboardType="numeric"
            maxLength={10}
            returnKeyType="done"
          />
          {dob.length === 10 && !dobValid && (
            <Text style={styles.fieldHint}>Enter a valid date — must be 18 to 60 years old</Text>
          )}
        </View>

        {/* Marital Status */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Marital Status <Text style={styles.required}>*</Text></Text>
          <View style={styles.chipGrid}>
            {MARITAL_STATUSES.map(ms => (
              <TouchableOpacity
                key={ms}
                style={[styles.chip, maritalStatus === ms && styles.chipActive]}
                onPress={() => setMaritalStatus(prev => prev === ms ? '' : ms)}
                activeOpacity={0.8}>
                <Text style={[styles.chipText, maritalStatus === ms && styles.chipTextActive]}>{ms}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Religion */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Religion <Text style={styles.required}>*</Text></Text>
          <View style={styles.chipGrid}>
            {RELIGIONS.map(r => (
              <TouchableOpacity
                key={r}
                style={[styles.chip, religion === r && styles.chipActive]}
                onPress={() => onReligionSelected(r)}
                activeOpacity={0.8}>
                <Text style={[styles.chipText, religion === r && styles.chipTextActive]}>{r}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Caste / Community (optional) */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Caste / Community</Text>
          <DP
            value={otherCaste ? 'Other' : caste}
            options={casteOptions}
            placeholder="Select Caste/Community (optional)"
            loading={loadingCommunities}
            labelMap={casteLabels}
            onSelect={v => {
              if (v === 'Other') { setOtherCaste(true); setCaste(''); }
              else { setOtherCaste(false); setCaste(v); }
            }}
          />
          {otherCaste && (
            <View style={styles.otherInput}>
              <TextInput
                style={styles.input}
                value={caste}
                onChangeText={setCaste}
                placeholder="Type your caste/community..."
                placeholderTextColor={THEME.textLight}
              />
            </View>
          )}
        </View>

        {/* State */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>State <Text style={styles.required}>*</Text></Text>
          <DP
            value={stateName}
            options={states.map(s => s.en)}
            placeholder="Select State"
            loading={loadingStates}
            labelMap={stateLabels}
            onSelect={onStateSelected}
          />
        </View>

        {/* District */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>District <Text style={styles.required}>*</Text></Text>
          <DP
            value={district}
            options={districts.map(d => d.en)}
            placeholder={!stateName ? 'Select state first' : 'Select District'}
            disabled={!stateName}
            loading={loadingDistricts}
            labelMap={districtLabels}
            onSelect={setDistrict}
          />
        </View>

        {/* Get Started button */}
        <TouchableOpacity
          style={[styles.submitBtn, (!canSubmit || saving) && styles.submitBtnDisabled]}
          onPress={handleGetStarted}
          disabled={!canSubmit || saving}
          activeOpacity={0.85}>
          {saving
            ? <ActivityIndicator color="#fff" />
            : <Text style={styles.submitBtnText}>Get Started →</Text>
          }
        </TouchableOpacity>

        {!canSubmit && !emailConflict && (
          <Text style={styles.hint}>Please fill in all fields marked with * to continue</Text>
        )}

        {emailConflict && (
          <View style={styles.conflictBox}>
            <Icon name="warning-outline" size={22} color="#92400E" />
            <Text style={styles.conflictText}>
              An account with this email already exists. Please sign in again with correct login to access your existing profile.
            </Text>
            <TouchableOpacity style={styles.conflictBtn} onPress={relogin}>
              <Text style={styles.conflictBtnText}>Sign In Again</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={logout}>
              <Text style={styles.conflictLogout}>Sign out instead</Text>
            </TouchableOpacity>
          </View>
        )}

      </ScrollView>
    </SafeAreaView>
  );
};

export default OnboardingScreen;

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: THEME.bg },
  content: { padding: 24, paddingBottom: 48 },

  header: { alignItems: 'center', marginBottom: 32, marginTop: 16 },
  appName: { fontSize: 28, fontWeight: '900', color: THEME.primary, letterSpacing: 0.5 },
  tagline: { fontSize: 20, fontWeight: '800', color: THEME.text, marginTop: 12 },
  subtitle: { fontSize: 14, color: THEME.textLight, marginTop: 6 },

  section: {
    backgroundColor: THEME.card, borderRadius: 16, padding: 16,
    marginBottom: 16,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2,
  },
  sectionLabel: { fontSize: 15, fontWeight: '700', color: THEME.text, marginBottom: 12 },
  required: { color: THEME.danger },

  genderRow: { flexDirection: 'row', gap: 12 },
  genderCard: {
    flex: 1, alignItems: 'center', paddingVertical: 20, borderRadius: 14,
    borderWidth: 2, borderColor: THEME.border, backgroundColor: THEME.bg,
    position: 'relative',
  },
  genderCardActive: { borderColor: THEME.primary, backgroundColor: '#FFF0F5' },
  genderIcon: { fontSize: 36, marginBottom: 8 },
  genderLabel: { fontSize: 16, fontWeight: '700', color: THEME.textLight },
  genderLabelActive: { color: THEME.primary },
  genderCheck: { position: 'absolute', top: 8, right: 8 },

  input: {
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 12,
    fontSize: 16, color: THEME.text, backgroundColor: THEME.bg,
  },
  inputReadonly: { backgroundColor: '#F0F0F0', color: THEME.textLight },

  otherInput: { marginTop: 8 },
  chipGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20,
    borderWidth: 1.5, borderColor: THEME.border, backgroundColor: THEME.bg,
  },
  chipActive: { backgroundColor: THEME.primary, borderColor: THEME.primary },
  chipText: { fontSize: 13, fontWeight: '600', color: THEME.textLight },
  chipTextActive: { color: '#fff' },

  submitBtn: {
    backgroundColor: THEME.primary, borderRadius: 14,
    paddingVertical: 16, alignItems: 'center', marginTop: 8,
  },
  submitBtnDisabled: { opacity: 0.45 },
  submitBtnText: { color: '#fff', fontSize: 17, fontWeight: '800' },
  hint: { textAlign: 'center', fontSize: 13, color: THEME.textLight, marginTop: 12 },
  fieldHint: { fontSize: 12, color: THEME.danger, marginTop: 6 },

  conflictBox: {
    marginTop: 16, padding: 16, borderRadius: 14,
    backgroundColor: '#FFF3CD', borderWidth: 1.5, borderColor: '#F59E0B',
    alignItems: 'center', gap: 10,
  },
  conflictText: { fontSize: 14, color: '#92400E', textAlign: 'center', lineHeight: 20 },
  conflictBtn: {
    paddingVertical: 12, paddingHorizontal: 32, borderRadius: 12,
    backgroundColor: THEME.primary, marginTop: 4,
  },
  conflictBtnText: { color: '#fff', fontWeight: '800', fontSize: 15 },
  conflictLogout: { fontSize: 13, color: THEME.textLight, textDecorationLine: 'underline', marginTop: 4 },
});
